import { useQuery } from 'data-fetching-client';
import { useEffect, useState } from 'react';

/**
 * Custom hook to perform a debounced query.
 *
 * @param {number} timeout - The debounce timeout in milliseconds.
 * @param {RegisteredQuery<TFieldName, TVariables, TResponse>} query - The name of the registered data-fetching-client query to be executed.
 * @param {Parameters<typeof useQuery<TFieldName, TVariables, TResponse>>[1]} [options] - Any options being sent to useQuery - please memoize this object!
 *
 * @returns {ReturnType<typeof useQuery<TFieldName, TVariables, TResponse>>} The query result.
 *
 * @example
 * const options = useMemo(() => ({ variables: { searchTerm: 'example' } }), [searchTerm]);
 * const { data, loading, error } = useDebouncedQuery(300, MY_QUERY, options);
 *
 * @note It is recommended to memoize the options object to prevent unnecessary re-renders.
 */
function useDebouncedQuery(timeout, query, options) {
  const [debouncedOptions, setDebouncedOptions] = useState(options);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedOptions(options);
    }, timeout);
    return () => {
      clearTimeout(handler);
    };
  }, [options, timeout]);
  return useQuery(query, Object.assign({}, debouncedOptions));
}
export default useDebouncedQuery;